"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const array_flatten_1 = __importDefault(require("array-flatten"));
const earcut_1 = __importDefault(require("earcut"));
const cdt2d_1 = __importDefault(require("cdt2d"));
const common_1 = require("../common");
/** Use cdt2d unless this flag is set. Note that earcut removes collinear points. */
const USE_EARCUT = false;
/**
 * Uses the earcut library to compute a triangulation of the given 2D general polygon.
 * @param polygon A 2D general polygon: a list of 2D point loops, the first being the outer
 * boundary and the rest being holes.
 * @returns A list of triangles specified by their vertex indices.
 */
function triangulatePolygonUsingEarcut(polygon) {
    // Convert from a general polygon to the representation used by earcut.
    const flattenedPoints = array_flatten_1.default(polygon);
    const loopCounts = polygon.map(loop => loop.length);
    const loopStartIndices = loopCounts.map((_, i) => common_1.utils.sum(loopCounts.slice(0, i)));
    const holeStartIndices = loopStartIndices.slice(1);
    // Call earcut to compute a triangulation.
    const triangulation = earcut_1.default(flattenedPoints, holeStartIndices);
    // Group triplets of indices.
    const indexTriplets = [];
    for (let i = 0; i < triangulation.length; i += 3) {
        indexTriplets.push([triangulation[i], triangulation[i + 1], triangulation[i + 2]]);
    }
    return indexTriplets;
}
/**
 * Finds a triplet of vertex indices where the first vertex is unmerged and the second vertex is
 * merged.
 * @param originalVertexLoops The vertex index loops of a general polygon.
 * @param mergeVertexMapping The mapping from original vertex indices to merged vertex indices.
 * @returns A triplet of vertex indices, or undefined if none can be found.
 */
function findMergedVertexSequence(originalVertexLoops, mergeVertexMapping) {
    const isMerged = (vertexIndex) => mergeVertexMapping[vertexIndex] !== vertexIndex;
    for (const loop of originalVertexLoops) {
        const n = loop.length;
        for (let j = 0; j < n; j++) {
            const v1 = loop[j];
            const v2 = loop[(j + 1) % n];
            const v3 = loop[(j + 2) % n];
            if (!isMerged(v1) && isMerged(v2)) {
                return [v1, v2, v3];
            }
        }
    }
    return undefined;
}
/**
 * Finds a triangle that includes an edge connecting the specified vertices.
 * @param triangulation A triangulation.
 * @param v1 The first vertex index.
 * @param v2 The second vertex index.
 * @returns A triangle and the index within the triangle of the edge start, or undefined if no such
 * triangle can be found.
 */
function findTriangleWithEdge(triangulation, v1, v2) {
    for (const triangle of triangulation) {
        for (const i of [0, 1, 2]) {
            if (triangle[i] === v1 && triangle[(i + 1) % 3] === v2) {
                return { triangle, i };
            }
        }
    }
    return undefined;
}
/**
 * Uses the cdt2d library to compute a Delaunay triangulation of the given 2D general polygon.
 * @param polygon A 2D general polygon: a list of 2D point loops, the first being the outer
 * boundary and the rest being holes.
 * @returns A list of triangles specified by their vertex indices.
 */
function triangulatePolygonUsingCdt2d(polygon) {
    // Get a flattened list of points.
    const points = array_flatten_1.default.depth(polygon, 1);
    if (points.length < 3) {
        return [];
    }
    // Calculate a distance tolerance relative to the size of the polygon bounds.
    const bounds = common_1.geom.getBounds(polygon[0]);
    const distanceScale = common_1.vec.distance(bounds.min, bounds.max);
    const tolerance = 1e-5 * distanceScale;
    // Create a mapping from original vertex indices to merged vertex indices, where we merge to
    // the lowest vertex index whose position is whithin tolerance of the original point.
    const mergeVertexMapping = points.map(p => points.findIndex(q => common_1.vec.distance(q, p) < tolerance));
    // Generate the list of edges needed by cdt2d. Use vertex indices that don't refer to any
    // duplicate points.
    const edges = [];
    const originalVertexLoops = [];
    let loopStart = 0;
    for (const loop of polygon) {
        const loopCount = loop.length;
        const originalVertexLoop = [];
        for (let i = 0; i < loopCount; i++) {
            const v1 = loopStart + i;
            const v2 = loopStart + (i + 1) % loopCount;
            originalVertexLoop.push(v1);
            const w1 = mergeVertexMapping[v1];
            const w2 = mergeVertexMapping[v2];
            // Don't include the reversed version of an existing edge.
            if (!edges.find(e => e[0] === w2 && e[1] === w1)) {
                edges.push([w1, w2]);
            }
        }
        loopStart += loopCount;
        originalVertexLoops.push(originalVertexLoop);
    }
    // Remove points and update indices to account for merged vertices.
    for (let vertexIndex = points.length - 1; vertexIndex >= 0; vertexIndex--) {
        if (mergeVertexMapping[vertexIndex] !== vertexIndex) {
            points.splice(vertexIndex, 1);
            for (const edge of edges) {
                for (const i of [0, 1]) {
                    if (edge[i] > vertexIndex) {
                        edge[i]--;
                    }
                }
            }
        }
    }
    // Call cdt2d to compute a constrained Delaunay triangulation.
    const triangulation = cdt2d_1.default(points, edges, { exterior: false });
    // Update indices in the triangulation to account for merged vertices.
    mergeVertexMapping.forEach((newVertexIndex, oldVertexIndex) => {
        if (newVertexIndex !== oldVertexIndex) {
            for (const triangle of triangulation) {
                for (const i of [0, 1, 2]) {
                    if (triangle[i] >= oldVertexIndex) {
                        triangle[i]++;
                    }
                }
            }
        }
    });
    // Split any vertices that were merged.
    const notDone = true; // make eslint happy
    while (notDone) {
        // Find a sequence of three input vertices starting with a non-merged vertex, going
        // to a merged vertex, and ending on any vertex. We're going to split apart the merged
        // vertex in the middle of this sequence.
        const vertexSequence = findMergedVertexSequence(originalVertexLoops, mergeVertexMapping);
        if (!vertexSequence) {
            // No merged vertices found.
            break;
        }
        // Process each of the triangles incident to v2, starting from the v1-v2 edge and ending
        // with the v2-v3 edge.
        const [v1, v2, v3] = vertexSequence;
        const v2Merged = mergeVertexMapping[v2];
        let currentVertex = v1;
        while (currentVertex !== mergeVertexMapping[v3]) {
            // Find the triangle that contains the edge from v1 to v2Merged.
            const triangleSearchResult = findTriangleWithEdge(triangulation, currentVertex, v2Merged);
            if (!triangleSearchResult) {
                // Something went wrong, but we'll let it go.
                break;
            }
            // Update the triangle to use v2 instead of v2Merged, then move on to the next
            // adjacent triangle that's incident to v2.
            const { triangle, i } = triangleSearchResult;
            triangle[(i + 1) % 3] = v2;
            currentVertex = triangle[(i + 2) % 3];
        }
        // Update v2 to indicate that it's no longer combined with v2Merged. Now we can use v2 as
        // the first vertex in a new three-vertex sequence.
        mergeVertexMapping[v2] = v2;
    }
    return triangulation;
}
/**
 * Returns a triangulation of the given 2D general polygon.
 * @param polygon A 2D general polygon: a list of 2D point loops, the first being the outer
 * boundary and the rest being holes.
 * @returns A list of triangles specified by their vertex indices.
 */
function triangulatePolygon(polygon) {
    // Compute the triangulation of the polygon.
    let triangulation;
    if (USE_EARCUT) {
        triangulation = triangulatePolygonUsingEarcut(polygon);
    }
    else {
        triangulation = triangulatePolygonUsingCdt2d(polygon);
    }
    // Make sure the triangles all have the same orientation.
    const points = array_flatten_1.default.depth(polygon, 1);
    const triangleAreas = triangulation.map(triangle => common_1.geom.getSignedArea(triangle.map(vertexIndex => points[vertexIndex])));
    if (!triangleAreas.every(area => area >= 0)) {
        throw new Error('Inconsistent triangle orientations.');
    }
    // Make sure the total area of the polygon and the total area of the triangles agree.
    const polygonArea = common_1.utils.sum(polygon, loop => common_1.geom.getSignedArea(loop));
    const triangulationArea = common_1.utils.sum(triangleAreas);
    if (Math.abs(polygonArea - triangulationArea) > 0.005) {
        throw new Error('Triangulation area does not match polygon area.');
    }
    return triangulation;
}
exports.triangulatePolygon = triangulatePolygon;
//# sourceMappingURL=triangulatePolygon.js.map